import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { floorplannerStore } from '../../store/floorplannerStore';
import {
  DEFAULT_SYMBOL_LIBRARY_TAB,
  isSymbolLibraryAssetDisabledIn3D,
  isSymbolLibraryTabDisabledIn3D,
  LibrarySymbolAsset,
  SymbolLibraryTabBar,
  SymbolLibrarySections,
} from './SymbolLibraryTabs';

import { editorStore } from '../../store/editorStore';
import { IoClose } from 'react-icons/io5';
import { observer } from 'mobx-react-lite';
import { invalidate } from '@react-three/fiber';

interface DropdownProps {
  position: { top: number; left: number };
  onClose: () => void;
  dropdownRef: React.Ref<HTMLDivElement>;
}

export const SymbolsPanel: React.FC<DropdownProps> = observer(({ position, onClose, dropdownRef }) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<string>(editorStore.symbolLibraryActiveTab);
  const [search, setSearch] = useState('');
  const displayedTab = editorStore.view3D && isSymbolLibraryTabDisabledIn3D(activeTab)
    ? DEFAULT_SYMBOL_LIBRARY_TAB
    : activeTab;

  // Prevent clicks inside the panel from closing it
  const handlePanelClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();  // Prevents the event from bubbling up
  };

  const handleIconClick = (label: string, props?: any) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (floorplannerStore.lockedByOther) return;
    if (editorStore.view3D && ["Door", "SlidingDoor", "GlassSlidingDoor", "DoubleDoor", "Window", "Void"].includes(label)) {
      return;
    }

    // Retrieve the visible center from the editor store
    const [centerX, centerY] = editorStore.getVisibleCenter();
    let insertedSymbolId = '';
    /*   if (label === "Wall") {
        // Set a default wall length, for example 5 units (adjust based on your needs)
        const wallLength = 2;
  
        // Calculate the start and end points of the wall, centered around the visible center
        const startX = centerX - wallLength / 2;
        const endX = centerX + wallLength / 2;
        const startY = centerY;
        const endY = centerY;
  
        const wall: WallType = {
          id: floorplannerStore.generateId(),
          type: "wall",
          start: new Vector2(startX, startY), // Start point to the left of the center
          end: new Vector2(endX, endY), // End point to the right of the center
  
        }
        floorplannerStore.addWall(wall);
      } else if (label === "SingleLine") {
        // Set a default line length, for example 5 units (adjust based on your needs)
        const singleLineLength = 2;
        // Calculate the start and end points of the wall, centered around the visible center
        const startX = centerX - singleLineLength / 2;
        const endX = centerX + singleLineLength / 2;
        const startY = centerY;
        const endY = centerY;
  
        const line: SingleLineType = {
          id: floorplannerStore.generateId(),
          type: "singleLine",
          start: new Vector2(startX, startY), // Start point to the left of the center
          end: new Vector2(endX, endY), // End point to the right of the center
        }
        floorplannerStore.addSingleLine(line);
      } else if (label === "RulerLine") {
        // Set a default line length, for example 5 units (adjust based on your needs)
        const rulerLineLength = 2;
        // Calculate the start and end points of the wall, centered around the visible center
        const startX = centerX - rulerLineLength / 2;
        const endX = centerX + rulerLineLength / 2;
        const startY = centerY;
        const endY = centerY;
  
        const line: RulerLineType = {
          id: floorplannerStore.generateId(),
          type: "rulerLine",
          start: new Vector2(startX, startY), // Start point to the left of the center
          end: new Vector2(endX, endY), // End point to the right of the center
        }
        floorplannerStore.addRulerLine(line);
      } else if (label === "WallConstructionMode") {
        editorStore.setWallConstructionMode(!editorStore.wallConstructionMode);
        onClose();
      } else if (label === "LineConstructionMode") {
        editorStore.setLineConstructionMode(!editorStore.lineConstructionMode);
        onClose();
      } else if (label === "RulerConstructionMode") {
        editorStore.setRulerConstructionMode(!editorStore.rulerConstructionMode);
        onClose();
      }  else if (label === "AreaConstructionMode") {
        editorStore.setAreaConstructionMode(!editorStore.areaConstructionMode);
        onClose();
      }  */
    if (label === "Door") {
      insertedSymbolId = floorplannerStore.addSymbol("door", [centerX, centerY], props);
    }
    else if (label === "SlidingDoor") {
      insertedSymbolId = floorplannerStore.addSymbol("slidingDoor", [centerX, centerY], props);
    }
    else if (label === "GlassSlidingDoor") {
      insertedSymbolId = floorplannerStore.addSymbol("slidingDoor", [centerX, centerY], {
        ...props,
        fillPattern: "glass",
        libraryKey: props?.libraryKey ?? "MANUAL/GlassSlidingDoor",
      });
    }
    else if (label === "DoubleDoor") {
      insertedSymbolId = floorplannerStore.addSymbol("doubleDoor", [centerX, centerY], props);
    } else if (label === "CircleStairs") {
      insertedSymbolId = floorplannerStore.addSymbol("circleStairs", [centerX, centerY], props);
    } else if (label === "RectStairs") {
      insertedSymbolId = floorplannerStore.addSymbol("rectStairs", [centerX, centerY], props);
    } else if (label === "TurnStairs") {
      insertedSymbolId = floorplannerStore.addSymbol("turnStairs", [centerX, centerY], props);
    } else if (label === "SquareSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("square", [centerX, centerY], props);
    } else if (label === "CircleSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("circle", [centerX, centerY], props);
    } else if (label === "TriangleSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("triangle", [centerX, centerY], props);
    } else if (label === "PolygonSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("polygon", [centerX, centerY], props);
    } else if (label === "SmartTableSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("smartTable", [centerX, centerY], props);
    } else if (label === "BookShelfSymbol") {
      insertedSymbolId = floorplannerStore.addSymbol("bookShelf", [centerX, centerY], props);
    } else if (label === "SvgSymbol") {
      // Use the flipped property from props (which comes from FILE_SVG_OPTIONS)
      // Default to false if not specified to match the new behavior
      const svgProps = { ...props, flipped: props?.flipped !== undefined ? props.flipped : false };
      insertedSymbolId = floorplannerStore.addSymbol("svg", [centerX, centerY], svgProps);
    }
    /* else if (label === "TextSymbol") {
      floorplannerStore.addSymbol("text", [0, 0]);
    }  */
    else if (label === "Window") {
      insertedSymbolId = floorplannerStore.addSymbol("window", [centerX, centerY], props);
    } else if (label === "Void") {
      insertedSymbolId = floorplannerStore.addSymbol("void", [centerX, centerY], props);
    } /* else if (label === "AreaSymbol") {
      floorplannerStore.addSymbol("area", [0, 0], {
        vertices: [
          new Vector2(0, 0),
          new Vector2(1, 0),
          new Vector2(1.5, 0.5),
          new Vector2(1, 1),
          new Vector2(0, 1),
        ],
      });
    } */

    const insertedSymbol = floorplannerStore.symbolsMap.get(insertedSymbolId);
    if (insertedSymbol) {
      editorStore.setSelections([insertedSymbol]);
      invalidate();
    }
  };

  const handleLibrarySymbolSelect = (symbol: LibrarySymbolAsset) => {
    // Another collaborator holds the edit lease, so this view is read-only.
    if (floorplannerStore.lockedByOther) return;
    if (editorStore.view3D && isSymbolLibraryAssetDisabledIn3D(symbol)) {
      return;
    }

    // Handle legacy symbols (Door, Window, Stairs, SvgSymbol with custom options, etc.)
    // Do not force lockAspectRatio here — openings default unlocked via
    // getDefaultPropertiesForType; only pass through an explicit library override.
    if (symbol.legacyType) {
      handleIconClick(symbol.legacyType, {
        ...symbol.svgOptions,
        libraryKey: symbol.id,
      });
    } else {
      // File-library SVG furniture/symbols stay locked unless the asset opts out.
      const svgOptions = {
        ...symbol.svgOptions,
        svgPath: symbol.src,
        svgLength: symbol.svgOptions?.svgLength || 2,
        svgHeight: symbol.svgOptions?.svgHeight || 2,
        lockAspectRatio: symbol.svgOptions?.lockAspectRatio ?? true,
        name: symbol.label,
        libraryKey: symbol.id,
        // Pass through the flipped property from FILE_SVG_OPTIONS
        flipped: symbol.svgOptions?.flipped !== undefined ? symbol.svgOptions.flipped : false,
      };
      handleIconClick("SvgSymbol", svgOptions);
    }
  };

  return (
    <div
      className="fixed z-50 w-[398px] bg-white rounded shadow-lg mb-8"
      style={{ top: position.top, left: position.left, maxHeight: '70vh' }}
      ref={dropdownRef}
      onClick={handlePanelClick}
    >
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-normal">{t('panels.symbolLibrary')}</div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-500"
          >
            <IoClose style={{ strokeWidth: 0.5 }} size={24} />
          </button>
        </div>
      </div>

      <div className="border-t border-[#edebe6]"></div>

      <div className="px-4 py-3">
        <SymbolLibraryTabBar
          activeTab={displayedTab}
          onTabChange={(tab) => {
            if (editorStore.view3D && isSymbolLibraryTabDisabledIn3D(tab)) {
              return;
            }
            setActiveTab(tab);
            editorStore.setSymbolLibraryActiveTab(tab);
          }}
          is3DView={editorStore.view3D}
        />
      </div>

      <div className="border-t border-[#edebe6]"></div>

      <div className="px-4 py-3">
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('symbols.search')}
          className="h-9 w-full rounded border border-[#edebe6] px-3 text-sm outline-none focus:border-gray-400"
        />
      </div>

      <div className="border-t border-[#edebe6]"></div>

      {displayedTab === "ELEVATION" && (
        <div className="px-4 pt-3 text-xs font-normal text-gray-500">
          {t("structures.elevationUnavailableIn3D")}
        </div>
      )}

      <SymbolLibrarySections
        tab={displayedTab}
        search={search}
        onSelect={handleLibrarySymbolSelect}
        className={`mt-4${floorplannerStore.lockedByOther ? ' opacity-50' : ''}`}
        scrollPosition={editorStore.getSymbolLibraryScrollPosition(displayedTab)}
        onScrollChange={(position) => editorStore.setSymbolLibraryScrollPosition(displayedTab, position)}
        is3DView={editorStore.view3D}
      />

    </div>

  );
});
